import { Box, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import PageHero from '../shared/PageHero'
import Section from '../shared/Section'
import SectionTitle from '../shared/SectionTitle'
import TvButton from '../shared/TvButton'
import CtaBanner from '../shared/CtaBanner'
import { COLORS } from '../../constants'
import { mentorship } from '../../utils/siteContent'
import usePageMeta from '../../utils/usePageMeta'

const MENTORSHIP_CONTACT = `/contact?topic=${encodeURIComponent('Mentorship')}`

function Mentorship() {
    usePageMeta({
        title: 'Developer Mentorship: Code Reviews, React & AWS Guidance',
        description: 'One-on-one software engineering mentorship covering code reviews, architecture, AWS, React and TypeScript, debugging and automated testing, for CS students, aspiring developers, and junior and mid-level engineers.',
        path: '/mentorship',
    })

    return (
        <>
            <PageHero
                eyebrow="Developer mentorship"
                title="Become the engineer your team relies on"
                intro="Practical, one-on-one mentorship from an engineer who still ships production code. We work on your real code and real problems, whether that’s your job, your coursework or your first projects."
                actions={
                    <>
                        <TvButton text="Ask About Mentorship" to={MENTORSHIP_CONTACT} icon={<ArrowForwardIcon />} />
                        <TvButton text="What we cover" link="#topics" variant="outline" tone="dark" />
                    </>
                }
            />

            <Section id="topics">
                <SectionTitle eyebrow="What we work on" text="Skills that make you more independent" intro="Every session is shaped around what you’re working on, but these are the areas I help with most." />
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' } }}>
                    {mentorship.topics.map((t) => (
                        <Box key={t.title} sx={{ p: 3, borderRadius: 3, border: `1px solid ${COLORS.border}` }}>
                            <Typography variant="h4" component="h3">{t.title}</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{t.body}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>

            <Section tone="light">
                <SectionTitle eyebrow="Ways to work together" text="Pick what fits" />
                <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
                    {mentorship.formats.map((f, i) => (
                        <Box
                            key={f.title}
                            sx={{
                                p: 3.5, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 2,
                                backgroundColor: i === 1 ? COLORS.black : COLORS.white,
                                color: i === 1 ? COLORS.white : COLORS.black,
                                border: `1px solid ${i === 1 ? COLORS.black : COLORS.border}`,
                            }}
                        >
                            <Typography variant="h3">{f.title}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
                                <Typography component="p" sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{f.price}</Typography>
                                <Typography component="p" variant="body2" sx={{ color: i === 1 ? COLORS.mutedOnDark : 'text.secondary' }}>{f.priceNote}</Typography>
                            </Box>
                            <Typography sx={{ color: i === 1 ? COLORS.mutedOnDark : 'text.secondary' }}>{f.body}</Typography>
                            <Box component="dl" sx={{ m: 0, flexGrow: 1, display: 'grid', gap: 1.25, alignContent: 'start', pt: 2, borderTop: `1px solid ${i === 1 ? COLORS.blackBorder : COLORS.border}` }}>
                                {f.details.map((d) => (
                                    <Box key={d.label}>
                                        <Typography component="dt" sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: i === 1 ? COLORS.accent : COLORS.accentOnLight }}>
                                            {d.label}
                                        </Typography>
                                        <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.25 }}>{d.value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Box>
                                <TvButton text="Get started" to={MENTORSHIP_CONTACT} variant={i === 1 ? 'primary' : 'outline'} tone={i === 1 ? 'dark' : 'light'} size="small" />
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
                    To get started, send a quick note through the contact form. We’ll confirm it’s a good fit and find a time over email.
                </Typography>
            </Section>

            <Section>
                <Box sx={{ display: 'grid', gap: { xs: 6, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                    <Box>
                        <SectionTitle eyebrow="Who it’s for" text="A good fit if you’re…" />
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: -2, display: 'grid', gap: 1.5 }}>
                            {mentorship.audiences.map((a) => (
                                <Box component="li" key={a} sx={{ display: 'flex', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: COLORS.accentOnLight, mt: 0.4, flexShrink: 0 }} fontSize="small" />
                                    <Typography>{a}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box>
                        <SectionTitle eyebrow="Why me" text="I’ve been on both sides" />
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: -2, display: 'grid', gap: 1.5 }}>
                            {mentorship.background.map((b) => (
                                <Box component="li" key={b} sx={{ display: 'flex', gap: 1.5 }}>
                                    <CheckIcon sx={{ color: COLORS.accentOnLight, mt: 0.4, flexShrink: 0 }} fontSize="small" />
                                    <Typography>{b}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Section>

            <CtaBanner
                title="Want an experienced engineer in your corner?"
                body="Tell me where you are, what you’re working on and where you want to be. I’ll let you know how I can help."
                buttonText="Ask About Mentorship"
                to={MENTORSHIP_CONTACT}
            />
        </>
    )
}

export default Mentorship
